/**
 * Kundenportal: „Nach der Feier nach meiner Meinung fragen“ (Google-Bewertungsbitte, sql/542).
 * Der Gast stimmt hier zu oder widerruft — jederzeit. Nur sichtbar, wenn der Betrieb die Funktion
 * eingeschaltet hat. Keine Vorauswahl: der Stand kommt aus dem Vorgang.
 */
import { useEffect, useState } from "react";
import { Toggle } from "./components";
import { customerPortalFetch, customerPortalApiError } from "../lib/customer-portal-client";

interface PortalLage {
  aktiv: boolean;
  zugestimmtAm: string | null;
  privacyUrl: string | null;
}

export function PortalBewertungSchalter({ eventId }: { eventId: string }) {
  const [stand, setStand] = useState<PortalLage | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  useEffect(() => {
    let lebt = true;
    customerPortalFetch(`/api/public/customer-portal/events/${eventId}/bewertung`)
      .then(async (r) => (r.ok ? ((await r.json()) as { data: PortalLage }).data : null))
      .then((d) => lebt && setStand(d))
      .catch(() => {});
    return () => {
      lebt = false;
    };
  }, [eventId]);

  if (!stand?.aktiv) return null;

  async function setzen(zustimmen: boolean) {
    setSpeichert(true);
    setFehler(null);
    try {
      const res = await customerPortalFetch(`/api/public/customer-portal/events/${eventId}/bewertung`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ zustimmen, sprache: "de" }),
      });
      if (!res.ok) throw await customerPortalApiError(res);
      setStand(((await res.json()) as { data: PortalLage }).data);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setSpeichert(false);
    }
  }

  return (
    <div className="mt-8 rounded-xl border border-outline-variant px-4 py-4 dark:border-white/10" data-testid="portal-bewertung">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-body-md text-body-md font-semibold text-on-surface dark:text-inverse-on-surface">Nach der Feier nach meiner Meinung fragen</p>
          <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            Wir schicken Ihnen drei Werktage nach der Feier einmal eine E-Mail und fragen, wie es Ihnen gefallen hat, mit der Bitte um eine Google-Bewertung. Freiwillig und jederzeit hier widerrufbar.
            {stand.privacyUrl && (
              <>
                {" "}
                <a href={stand.privacyUrl} target="_blank" rel="noopener noreferrer" className="underline">
                  Datenschutz
                </a>
              </>
            )}
          </p>
          {stand.zugestimmtAm && (
            <p className="mt-1 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
              Zugestimmt am {new Date(stand.zugestimmtAm).toLocaleDateString("de-DE", { timeZone: "Europe/Berlin" })}.
            </p>
          )}
          {fehler && <p className="mt-1 font-body-sm text-[12.5px] text-error">{fehler}</p>}
        </div>
        <Toggle checked={Boolean(stand.zugestimmtAm)} onChange={(v) => void setzen(v)} label="Nach der Feier nach meiner Meinung fragen" disabled={speichert} />
      </div>
    </div>
  );
}
